'use client';

import { faFacebook, faInstagram, faSnapchat, faTiktok, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faArrowLeft, faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Button } from '@pickleballinc/ui/components/ui/button';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';
import dayjs from 'dayjs';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DetailsRegistrationButton, IconText } from '@/components/atoms';
import { ContactLeagueButton } from '@/components/atoms/ContactLeagueButton';
import { SocialMediaLinks } from '@/components/atoms/SocialMediaLinks';
import { TEAM_LEAGUES_PLACEHOLDER_IMAGE } from '@/components/constants/constants';
import { TeamLeagueDetailHeaderNavButtons } from '@/components/molecules';
import { NextGamedayOrEvent } from '@/components/molecules/NextGamedayOrEvent';
import { ScheduledEventsModal } from '@/components/molecules/ScheduledEventsModal';
import { TeamLeagueDetailsHeaderSkeleton } from '@/components/molecules/skeletons/TeamLeagueDetailsHeaderSkeleton';
import { generateNavItems } from '@/components/molecules/TeamLeagueDetailHeaderNavButtons';
import { PermissionRole } from '@/entities/PermissionRoles';
import { TeamLeagueInfo } from '@/entities/TeamLeagueInfo';
import { useNextEventOrGamedayQuery } from '@/hooks/banner/useNextEventOrGameDayQuery';
import { useSeasonDistrictDivisionInfo } from '@/hooks/custom-hooks/useSeasonDistrictDivisionInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useSponsors } from '@/hooks/hooks';
import { publicEnv } from '@/lib/env';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { isLeagueRegistrationOpen } from '@/utils/helpers';
import { getLastPathSegment } from '@/utils/utils';

interface TeamLeagueDetailsHeaderProps {
	teamLeagueId: string;
	showOnDivision?: boolean;
}

export const TeamLeagueDetailsHeader = ({ teamLeagueId, showOnDivision = false }: TeamLeagueDetailsHeaderProps) => {
	const { canManageTeamLeague, userPermission } = usePermissionStore();
	const organizationStore = useOrganizationStore();
	const teamLeagueInfo = useTeamLeagueInfo();

	const { data: nextGamedayOrEvent, isLoading: isLoadingNextGamedayOrEvent } = useNextEventOrGamedayQuery(teamLeagueId, teamLeagueInfo.seasonUuid);
	const hasPermission = userPermission !== PermissionRole.NOT_AUTHORIZED;
	const { districtOptions, divisionOptions } = useSeasonDistrictDivisionInfo();

	const { data: sponsorsInfo, isLoading: isLoadingSponsors } = useSponsors(teamLeagueId);
	const router = useRouter();
	const searchParams = useSearchParams();

	const [isSticky, setIsSticky] = useState(false);
	const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);
	const pathname = usePathname();

	const mainHeaderRef = useRef<HTMLDivElement | null>(null);
	const [refReady, setRefReady] = useState(false);

	const setMainHeaderRef = useCallback((node: HTMLDivElement | null) => {
		if (node !== null) {
			mainHeaderRef.current = node;
			setRefReady(true);
		}
	}, []);

	const isUserPartOfLeague = teamLeagueInfo.isPartOfTeamLeague;
	const currentRoute = getLastPathSegment(pathname);
	const isRegistrationOpen = isLeagueRegistrationOpen(teamLeagueInfo.registrationDateOpen, teamLeagueInfo.registrationDateClose);

	const firstNavItem = generateNavItems(
		teamLeagueInfo.isSeasonActive ?? false,
		!districtOptions.length || districtOptions.length === 1,
		!divisionOptions.length || divisionOptions.length === 1,
		isRegistrationOpen,
		sponsorsInfo && sponsorsInfo?.data?.length !== 0
	)[0];

	const isFirstNavItemRoute =
		(firstNavItem?.href === '/' && currentRoute === '/') || (firstNavItem?.href === '/standings' && currentRoute === '/standings');

	const useRegularStyling = !isFirstNavItemRoute;

	useEffect(() => {
		setIsSticky(false);
		if (!mainHeaderRef.current || (currentRoute === '/division' && !showOnDivision)) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry) {
					setIsSticky(!entry.isIntersecting && window.scrollY > 0);
				}
			},
			{
				threshold: 0,
				rootMargin: '0px 0px 0px 0px'
			}
		);

		observer.observe(mainHeaderRef.current);

		const checkScrollPosition = () => {
			if (window.scrollY === 0) {
				setIsSticky(false);
			}
		};

		window.addEventListener('scroll', checkScrollPosition);

		return () => {
			if (mainHeaderRef.current) {
				observer.unobserve(mainHeaderRef.current);
			}
			window.removeEventListener('scroll', checkScrollPosition);
		};
	}, [currentRoute, showOnDivision, refReady]);

	if (isLoadingNextGamedayOrEvent || isLoadingSponsors) {
		return <TeamLeagueDetailsHeaderSkeleton />;
	}

	if (!teamLeagueInfo || (currentRoute === '/division' && !showOnDivision)) {
		return null;
	}

	const registerForNextSeasonUrl =
		organizationStore.organizationUuid && organizationStore.teamLeagueUuid
			? `${publicEnv.NEXT_PUBLIC_PTL_CHECKOUT_URL}/team-league/${organizationStore.organizationUuid}/${organizationStore.teamLeagueUuid}`
			: '#';

	const socialMediaLinks = [
		{ url: teamLeagueInfo.facebook, icon: faFacebook },
		{ url: teamLeagueInfo.twitter, icon: faXTwitter },
		{ url: teamLeagueInfo.instagram, icon: faInstagram },
		{ url: teamLeagueInfo.snapchat, icon: faSnapchat },
		{ url: teamLeagueInfo.youtube, icon: faYoutube },
		{ url: teamLeagueInfo.tiktok, icon: faTiktok }
	];

	const onNextSeasonClick = () => {
		const newSearchParams = new URLSearchParams(searchParams.toString());
		newSearchParams.set('season', teamLeagueInfo.nextSeason!.uuid);
		router.push(`?${newSearchParams.toString()}`, {
			scroll: false
		});
	};

	const onBackToCurrentSeasonClick = () => {
		const newSearchParams = new URLSearchParams(searchParams.toString());
		newSearchParams.set('season', teamLeagueInfo.seasonUuid);
		router.push(`?${newSearchParams.toString()}`, {
			scroll: false
		});
	};

	const isNextSeasonSelected = Boolean(teamLeagueInfo.nextSeason) && searchParams.get('season') === teamLeagueInfo.nextSeason?.uuid;

	const shouldDisplayRegisterForNextSeason =
		isNextSeasonSelected &&
		!!teamLeagueInfo.nextSeason?.showToPublic &&
		!!teamLeagueInfo.nextSeason?.useOnlineRegistration &&
		dayjs(teamLeagueInfo.nextSeason.showToPublicDateTime).isBefore(dayjs()) &&
		dayjs(dayjs()).isBetween(teamLeagueInfo.nextSeason?.registrationStartDate, teamLeagueInfo.nextSeason?.registrationEndDate);

	const shouldDisplayNextSeason = Boolean(
		teamLeagueInfo.nextSeason &&
			teamLeagueInfo.nextSeason?.showToPublic &&
			teamLeagueInfo.nextSeason?.useOnlineRegistration &&
			dayjs(teamLeagueInfo.nextSeason.showToPublicDateTime).isBefore(dayjs()) &&
			!isNextSeasonSelected
	);

	return (
		<>
			<div ref={setMainHeaderRef} className="bg-white border border-gray-200 rounded-lg shadow-sm">
				<div className="flex flex-col p-4 gap-2.5 sm:hidden">
					{!useRegularStyling && (
						<p className="text-gray-900 text-base font-semibold break-words whitespace-normal clamp-2-lines text-center">
							{teamLeagueInfo.title}
						</p>
					)}
					<Logo logoUrl={teamLeagueInfo.logo?.url} title={teamLeagueInfo.title} className="rounded-xl self-center max-h-48 w-fit" />
					{useRegularStyling ? (
						<p className="text-gray-900 text-base font-semibold break-words whitespace-normal clamp-2-lines text-center">
							{teamLeagueInfo.title}
						</p>
					) : null}

					{!teamLeagueInfo.hideAllRegistrationInfo && (
						<div className="flex items-center justify-center">
							<RegistrationBadge isRegistrationOpen={isRegistrationOpen} registeredTeamsCount={teamLeagueInfo.registeredTeamsCount} />
						</div>
					)}

					{teamLeagueInfo.dateStart && teamLeagueInfo.dateEnd && !teamLeagueInfo.hideSeasonStartDate ? (
						<div className="flex items-center justify-center">
							<LeagueDate
								dateStart={teamLeagueInfo.dateStart}
								dateEnd={teamLeagueInfo.dateEnd}
								iana={teamLeagueInfo.timezone.ianaTzIdentifier}
							/>
						</div>
					) : null}
					<TeamLeagueLinks />

					<div className="flex flex-col gap-2 items-center">
						<SocialMediaLinks
							socialMediaLinks={socialMediaLinks}
							className="flex flex-wrap gap-4"
							linkClassName="w-6 h-6 flex items-center justify-center"
						/>

						<div className="flex flex-row">
							{!!teamLeagueInfo.websiteUrl && (
								<CustomLink
									href={teamLeagueInfo.websiteUrl}
									target="_blank"
									className="text-sm not-italic font-medium leading-5 underline underline-offset-2"
								>
									Website
								</CustomLink>
							)}
							{!!teamLeagueInfo.ticketsUrl && (
								<div className="flex">
									{!!teamLeagueInfo.websiteUrl && <span className="px-2">|</span>}

									<CustomLink
										href={teamLeagueInfo.ticketsUrl}
										target="_blank"
										className="text-sm not-italic font-medium leading-5 underline underline-offset-2"
									>
										Tickets
									</CustomLink>
								</div>
							)}
						</div>
					</div>

					{shouldDisplayNextSeason ? (
						<Button
							className={clsx('font-semibold text-nowrap w-full md:w-52', {
								'text-xs h-8': useRegularStyling,
								'h-9': !useRegularStyling
							})}
							onClick={onNextSeasonClick}
						>
							Next Season
						</Button>
					) : null}
					{shouldDisplayRegisterForNextSeason ? (
						<Link href={registerForNextSeasonUrl} prefetch={false}>
							<Button
								className={clsx(
									'font-semibold flex flex-col w-full md:w-52 text-center whitespace-normal overflow-visible h-auto text-xs'
								)}
							>
								<span>Register For</span>
								<span className="text-xs font-normal">{teamLeagueInfo.nextSeason?.title}</span>
							</Button>
						</Link>
					) : null}
					{teamLeagueInfo.useOnlineRegistration ? (
						<DetailsRegistrationButton
							isRegistrationOpen={isRegistrationOpen}
							registrationEndDate={teamLeagueInfo.registrationDateClose}
							isUserPartOfLeague={isUserPartOfLeague}
							useRegularStyling={useRegularStyling}
							className="w-full lg:hidden"
						/>
					) : null}

					<ContactLeagueButton teamLeagueId={teamLeagueId} useRegularStyling={useRegularStyling} />

					{canManageTeamLeague || hasPermission ? (
						<Link
							className={clsx(
								'bg-black text-white text-nowrap w-full justify-center text-sm font-semibold flex gap-2 items-center py-2 px-3 rounded-lg',
								useRegularStyling && 'text-xs py-1 px-2'
							)}
							href={`/team-leagues/${teamLeagueId}/my-dashboard`}
							prefetch={false}
						>
							<FontAwesomeIcon icon={faEdit} className="size-4" />
							My Dashboard
						</Link>
					) : null}

					{isNextSeasonSelected ? (
						<Button
							variant="outline"
							onClick={onBackToCurrentSeasonClick}
							className={clsx('w-full md:w-52 flex gap-1', {
								'text-xs h-8': useRegularStyling,
								'h-9': !useRegularStyling
							})}
						>
							<FontAwesomeIcon icon={faArrowLeft} className="size-4" />
							Back To Current Season
						</Button>
					) : null}
				</div>
				{/* tablet & desktop */}
				<div className="hidden sm:flex flex-col py-8 px-6 gap-2.5">
					{!useRegularStyling && (
						<p className="text-gray-900 text-base font-semibold break-words whitespace-normal clamp-2-lines">{teamLeagueInfo.title}</p>
					)}
					<div className="flex justify-between">
						<div className="flex items-start gap-3">
							{teamLeagueInfo.logo?.url ? (
								<Logo
									logoUrl={teamLeagueInfo.logo?.url}
									title={teamLeagueInfo.title}
									className={clsx('rounded-xl', !useRegularStyling ? 'w-72' : 'h-24')}
								/>
							) : null}
							<div className={clsx('flex flex-col', !useRegularStyling ? 'gap-4' : 'gap-1.5')}>
								{!teamLeagueInfo.hideAllRegistrationInfo && !useRegularStyling && (
									<RegistrationBadge
										isRegistrationOpen={isRegistrationOpen}
										registeredTeamsCount={teamLeagueInfo.registeredTeamsCount}
									/>
								)}
								{useRegularStyling ? (
									<p className="text-gray-900 text-base font-semibold break-words whitespace-normal clamp-2-lines">
										{teamLeagueInfo.title}
									</p>
								) : null}
								{teamLeagueInfo.dateStart && teamLeagueInfo.dateEnd && !teamLeagueInfo.hideSeasonStartDate ? (
									<LeagueDate
										dateStart={teamLeagueInfo.dateStart}
										dateEnd={teamLeagueInfo.dateEnd}
										iana={teamLeagueInfo.timezone.ianaTzIdentifier}
									/>
								) : null}
								<TeamLeagueLinks />

								<div className="hidden sm:flex flex-row gap-4 flex-wrap">
									<SocialMediaLinks
										socialMediaLinks={socialMediaLinks}
										className="flex-wrap gap-4 flex"
										linkClassName="w-6 h-6 flex items-center justify-center"
									/>

									<div className="flex flex-row">
										{!!teamLeagueInfo.websiteUrl && (
											<CustomLink
												href={teamLeagueInfo.websiteUrl}
												target="_blank"
												className="text-sm not-italic font-medium leading-5 underline underline-offset-2"
											>
												Website
											</CustomLink>
										)}
										{!!teamLeagueInfo.ticketsUrl && (
											<div className="flex">
												{!!teamLeagueInfo.websiteUrl && <span className="px-2">|</span>}
												<CustomLink
													href={teamLeagueInfo.ticketsUrl}
													target="_blank"
													className="text-sm not-italic font-medium leading-5 underline underline-offset-2"
												>
													Tickets
												</CustomLink>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
						{/* desktop screens */}
						<TeamLeagueActions
							leagueInfo={teamLeagueInfo}
							isRegistrationOpen={isRegistrationOpen}
							isUserPartOfLeague={isUserPartOfLeague}
							canManageTeamLeague={canManageTeamLeague}
							hasPermission={hasPermission}
							teamLeagueId={teamLeagueId}
							isSticky={isSticky}
							useRegularStyling={useRegularStyling}
							shouldDisplayNextSeason={shouldDisplayNextSeason}
							onNextSeasonClick={onNextSeasonClick}
							isNextSeasonSelected={isNextSeasonSelected}
							onBackToCurrentSeasonClick={onBackToCurrentSeasonClick}
							shouldDisplayRegisterForNextSeason={shouldDisplayRegisterForNextSeason}
							registerForNextSeasonUrl={registerForNextSeasonUrl}
						/>
					</div>
					<div className="flex gap-4 xl:hidden overflow-x-auto no-scrollbar">
						{shouldDisplayNextSeason ? (
							<Button
								className={clsx('font-semibold text-nowrap w-full md:w-52', {
									'text-xs h-8': useRegularStyling,
									'h-9': !useRegularStyling
								})}
								onClick={onNextSeasonClick}
							>
								Next Season
							</Button>
						) : null}
						{shouldDisplayRegisterForNextSeason ? (
							<Link href={registerForNextSeasonUrl} prefetch={false}>
								<Button
									className={clsx(
										'font-semibold flex flex-col w-full md:w-52 text-center whitespace-normal overflow-visible h-auto'
									)}
								>
									<span>Register For</span>
									<span className="text-xs font-normal">{teamLeagueInfo.nextSeason?.title}</span>
								</Button>
							</Link>
						) : null}
						{teamLeagueInfo.useOnlineRegistration ? (
							<DetailsRegistrationButton
								isRegistrationOpen={isRegistrationOpen}
								registrationEndDate={teamLeagueInfo.registrationDateClose}
								isUserPartOfLeague={isUserPartOfLeague}
								useRegularStyling={useRegularStyling}
							/>
						) : null}
						<ContactLeagueButton teamLeagueId={teamLeagueId} useRegularStyling={useRegularStyling} />

						{canManageTeamLeague || hasPermission ? (
							<Link
								className={clsx(
									'bg-black text-white text-nowrap w-52 justify-center text-sm font-semibold flex gap-2 items-center py-2 px-3 rounded-lg',
									useRegularStyling && 'text-xs py-1 px-2'
								)}
								href={`/team-leagues/${teamLeagueId}/my-dashboard`}
								prefetch={false}
							>
								<FontAwesomeIcon icon={faEdit} className="size-4" />
								My Dashboard
							</Link>
						) : null}
						{isNextSeasonSelected ? (
							<Button
								variant="outline"
								onClick={onBackToCurrentSeasonClick}
								className={clsx('w-full md:w-52 flex gap-1', {
									'text-xs h-8': useRegularStyling,
									'h-9': !useRegularStyling
								})}
							>
								<FontAwesomeIcon icon={faArrowLeft} className="size-4" />
								Back To Current Season
							</Button>
						) : null}
					</div>
				</div>

				{nextGamedayOrEvent && nextGamedayOrEvent.statusCode === 200 && nextGamedayOrEvent.data.length > 0 ? (
					<div className="px-4 pb-4 md:pb-8 md:px-6">
						<NextGamedayOrEvent
							event={nextGamedayOrEvent.data[0]!}
							seeAllCount={nextGamedayOrEvent.data.length}
							onSeeAll={() => setIsEventsModalOpen(true)}
						/>
						{isEventsModalOpen && teamLeagueInfo.seasonUuid ? (
							<ScheduledEventsModal
								isOpen={isEventsModalOpen}
								onClose={() => setIsEventsModalOpen(false)}
								teamLeagueId={teamLeagueId}
								teamLeagueUuid={teamLeagueInfo.uuid}
								seasonUuid={teamLeagueInfo.seasonUuid}
							/>
						) : null}
					</div>
				) : null}

				<div className="w-full">
					<TeamLeagueDetailHeaderNavButtons
						hasPermission={hasPermission}
						teamLeagueId={teamLeagueId}
						shouldHideDistrics={!districtOptions.length || districtOptions.length === 1}
						shouldHideDivisions={!divisionOptions.length || divisionOptions.length === 1}
						isSeasonActive={teamLeagueInfo.isSeasonActive}
						isSeasonBtnVisible={sponsorsInfo ? sponsorsInfo?.data.length !== 0 : false}
						seasonStandingExist={teamLeagueInfo.seasonStandingsExist}
					/>
				</div>
			</div>

			<div
				className={`fixed left-0 top-0 z-50 w-full bg-white shadow-md transition-transform duration-500 ${
					isSticky ? 'translate-y-0' : '-translate-y-full'
				}`}
			>
				<div className="mx-auto flex max-w-screen-xl flex-col flex-wrap gap-4 bg-white p-3 sm:p-4 sm:px-6">
					<div className="flex justify-between items-center">
						<div className={clsx('flex', !useRegularStyling ? 'gap-3' : 'gap-4')}>
							{teamLeagueInfo.logo?.url ? (
								<Logo
									logoUrl={teamLeagueInfo.logo?.url}
									title={teamLeagueInfo.title}
									className={clsx('rounded-md', !useRegularStyling ? 'w-40 h-24' : 'w-40')}
								/>
							) : null}
							<div className="flex flex-col gap-4">
								{useRegularStyling ? (
									<div className="-mb-3 flex bg-white text-sm font-semibold text-gray-900 sm:text-base">{teamLeagueInfo.title}</div>
								) : null}

								{!useRegularStyling && (
									<div className="-mb-3 flex bg-white text-base font-semibold text-gray-900 sm:text-lg">{teamLeagueInfo.title}</div>
								)}
								{teamLeagueInfo.dateStart && teamLeagueInfo.dateEnd && !teamLeagueInfo.hideSeasonStartDate ? (
									<LeagueDate
										dateStart={teamLeagueInfo.dateStart}
										dateEnd={teamLeagueInfo.dateEnd}
										iana={teamLeagueInfo.timezone.ianaTzIdentifier}
									/>
								) : null}
								<SocialMediaLinks
									socialMediaLinks={socialMediaLinks}
									className="flex-wrap gap-4 hidden sm:flex"
									linkClassName="w-6 h-6 flex items-center justify-center"
								/>
							</div>
						</div>

						<TeamLeagueActions
							leagueInfo={teamLeagueInfo}
							isRegistrationOpen={isRegistrationOpen}
							isUserPartOfLeague={isUserPartOfLeague}
							canManageTeamLeague={canManageTeamLeague}
							hasPermission={hasPermission}
							teamLeagueId={teamLeagueId}
							isSticky={isSticky}
							useRegularStyling={useRegularStyling}
							shouldDisplayNextSeason={shouldDisplayNextSeason}
							onNextSeasonClick={onNextSeasonClick}
							isNextSeasonSelected={isNextSeasonSelected}
							onBackToCurrentSeasonClick={onBackToCurrentSeasonClick}
							shouldDisplayRegisterForNextSeason={shouldDisplayRegisterForNextSeason}
							registerForNextSeasonUrl={registerForNextSeasonUrl}
						/>
					</div>
					<div className="sm:hidden flex gap-2 flex-col">
						{shouldDisplayNextSeason ? (
							<Button
								className={clsx('font-semibold text-nowrap w-full md:w-52', {
									'text-xs h-8': useRegularStyling,
									'h-9': !useRegularStyling
								})}
								onClick={onNextSeasonClick}
							>
								Next Season
							</Button>
						) : null}
						{shouldDisplayRegisterForNextSeason ? (
							<Link href={registerForNextSeasonUrl} prefetch={false}>
								<Button
									className={clsx(
										'font-semibold flex flex-col w-full md:w-52 text-center whitespace-normal overflow-visible h-auto'
									)}
								>
									<span>Register For</span>
									<span className="text-xs font-normal">{teamLeagueInfo.nextSeason?.title}</span>
								</Button>
							</Link>
						) : null}
						{teamLeagueInfo.useOnlineRegistration ? (
							<DetailsRegistrationButton
								isRegistrationOpen={isRegistrationOpen}
								registrationEndDate={teamLeagueInfo.registrationDateClose}
								isUserPartOfLeague={isUserPartOfLeague}
								useRegularStyling={useRegularStyling}
								className="w-full lg:hidden"
							/>
						) : null}
						<ContactLeagueButton teamLeagueId={teamLeagueId} useRegularStyling={useRegularStyling} />

						{canManageTeamLeague || hasPermission ? (
							<Link
								className={clsx(
									'bg-black text-white text-nowrap w-full justify-center text-sm font-semibold flex gap-2 items-center py-2 px-3 rounded-lg',
									useRegularStyling && 'text-xs py-1 px-2'
								)}
								href={`/team-leagues/${teamLeagueId}/my-dashboard`}
								prefetch={false}
							>
								<FontAwesomeIcon icon={faEdit} className="size-4" />
								My Dashboard
							</Link>
						) : null}
						{isNextSeasonSelected ? (
							<Button
								variant="outline"
								onClick={onBackToCurrentSeasonClick}
								className={clsx('w-full md:w-52 flex gap-1', {
									'text-xs h-8': useRegularStyling,
									'h-9': !useRegularStyling
								})}
							>
								<FontAwesomeIcon icon={faArrowLeft} className="size-4" />
								Back To Current Season
							</Button>
						) : null}
					</div>
				</div>

				<div className="w-full bg-gray-soft">
					<div className="mx-auto w-full max-w-screen-xl">
						<TeamLeagueDetailHeaderNavButtons
							hasPermission={hasPermission}
							teamLeagueId={teamLeagueId}
							shouldHideDistrics={!districtOptions.length || districtOptions.length === 1}
							shouldHideDivisions={!divisionOptions.length || divisionOptions.length === 1}
							isSeasonActive={teamLeagueInfo.isSeasonActive}
							isSeasonBtnVisible={sponsorsInfo ? sponsorsInfo?.data.length !== 0 : false}
							seasonStandingExist={teamLeagueInfo.seasonStandingsExist}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

const Logo = ({ logoUrl, title, className }: { logoUrl?: string; title: string; className?: string }) => {
	return (
		<img
			className={clsx('object-contain transition-all duration-300', !logoUrl && 'h-[160px]', className ?? '')}
			src={getCDNURL(logoUrl ?? TEAM_LEAGUES_PLACEHOLDER_IMAGE, 400, 400)}
			alt={`${title} logo`}
		/>
	);
};

const RegistrationBadge = ({ isRegistrationOpen, registeredTeamsCount }: { isRegistrationOpen: boolean | null; registeredTeamsCount: number }) => {
	return (
		<div className="flex w-fit items-center rounded-md border border-gray-300 px-2 py-1 text-xs">
			<p className={`mr-1 size-[6px] rounded-full ${isRegistrationOpen ? ' bg-green-500' : ' bg-gray-300'}`} />
			<p className={`text-gray-600 ${isRegistrationOpen ? '' : 'text-gray-400'}`}>
				{isRegistrationOpen
					? `Reg open - ${registeredTeamsCount} team${registeredTeamsCount > 1 || !registeredTeamsCount ? 's' : ''}`
					: 'Registration closed'}
			</p>
		</div>
	);
};

const LeagueDate = ({ dateStart, dateEnd, iana }: { dateStart: Date; dateEnd: Date; iana: string }) => {
	const formatDateOnly = (date: Date) => {
		return dayjs(date).tz(iana).format('MMM D YYYY');
	};

	return (
		<div className="flex items-center text-sm">
			<IconText className="mr-2 size-5" icon={faCalendarDays} />
			{formatDateOnly(dateStart)} - {formatDateOnly(dateEnd)}
		</div>
	);
};

const TeamLeagueLinks = () => {
	// Hidden for know, since backend team needs to add support for contact forms and tickets/website
	return (
		<div className="hidden flex-wrap gap-4">
			<a>
				<p className="text-sm underline font-medium text-gray-900 cursor-pointer">Contact Team League</p>
			</a>
			<a>
				<p className="text-sm underline font-medium text-gray-900 cursor-pointer">Tickets</p>
			</a>
			<a>
				<p className="text-sm underline font-medium text-gray-900 cursor-pointer">Website</p>
			</a>
			<a>
				<p className="text-sm underline font-medium text-gray-900 cursor-pointer">Share this team league</p>
			</a>
		</div>
	);
};

const TeamLeagueActions = ({
	leagueInfo,
	isRegistrationOpen,
	isUserPartOfLeague,
	canManageTeamLeague,
	hasPermission,
	teamLeagueId,
	isSticky,
	useRegularStyling,
	shouldDisplayNextSeason,
	onNextSeasonClick,
	isNextSeasonSelected,
	onBackToCurrentSeasonClick,
	shouldDisplayRegisterForNextSeason,
	registerForNextSeasonUrl
}: {
	leagueInfo: TeamLeagueInfo;
	isRegistrationOpen: boolean | null;
	isUserPartOfLeague: boolean | null | undefined;
	canManageTeamLeague: boolean;
	hasPermission: boolean;
	teamLeagueId: string;
	isSticky?: boolean;
	useRegularStyling: boolean;
	shouldDisplayNextSeason: boolean;
	onNextSeasonClick: () => void;
	isNextSeasonSelected: boolean;
	onBackToCurrentSeasonClick: () => void;
	shouldDisplayRegisterForNextSeason: boolean;
	registerForNextSeasonUrl: string;
}) => {
	return (
		<div
			className={clsx(
				'flex-col',
				isSticky ? 'sm:flex hidden' : 'hidden xl:flex xl:justify-center',
				!useRegularStyling ? 'gap-4' : 'gap-3 items-end'
			)}
		>
			{shouldDisplayNextSeason ? (
				<Button
					className={clsx('font-semibold text-nowrap w-full md:w-52', {
						'text-xs h-8': useRegularStyling,
						'h-9': !useRegularStyling
					})}
					onClick={onNextSeasonClick}
				>
					Next Season
				</Button>
			) : null}
			{shouldDisplayRegisterForNextSeason ? (
				<Link href={registerForNextSeasonUrl} prefetch={false}>
					<Button className={clsx('font-semibold flex flex-col w-full md:w-52 text-center whitespace-normal overflow-visible h-auto')}>
						<span>Register For </span>
						<span className="text-xs font-normal">{leagueInfo.nextSeason?.title}</span>
					</Button>
				</Link>
			) : null}
			{leagueInfo.useOnlineRegistration ? (
				<DetailsRegistrationButton
					isRegistrationOpen={isRegistrationOpen}
					registrationEndDate={leagueInfo.registrationDateClose}
					isUserPartOfLeague={isUserPartOfLeague}
					useRegularStyling={useRegularStyling}
				/>
			) : null}

			<ContactLeagueButton teamLeagueId={teamLeagueId} useRegularStyling={useRegularStyling} />

			{canManageTeamLeague || hasPermission ? (
				<Link
					className={clsx(
						'bg-black text-white text-nowrap w-52 justify-center text-sm font-semibold flex gap-2 items-center py-2 px-3 rounded-lg',
						useRegularStyling && 'text-xs py-1 px-2'
					)}
					href={`/team-leagues/${teamLeagueId}/my-dashboard`}
					prefetch={false}
				>
					<FontAwesomeIcon icon={faEdit} className="size-4" />
					My Dashboard
				</Link>
			) : null}
			{isNextSeasonSelected ? (
				<Button
					variant="outline"
					onClick={onBackToCurrentSeasonClick}
					className={clsx('w-full md:w-52 flex gap-1', {
						'text-xs h-8': useRegularStyling,
						'h-9': !useRegularStyling
					})}
				>
					<FontAwesomeIcon icon={faArrowLeft} className="size-4" />
					Back To Current Season
				</Button>
			) : null}
		</div>
	);
};
